import {
    Box,
    Divider, Grid, Typography, useMediaQuery,
} from '@mui/material';
import {
    Business as BusinessIcon,
    EventAvailableOutlined as EventAvailableOutlinedIcon,
    LocationOnOutlined as LocationOnOutlinedIcon,
} from '@mui/icons-material';
import sanitizeHtml from 'sanitize-html';
import {
    memo, useContext, useEffect, useState, useCallback,
    useMemo,
} from 'react';
import markdownit from 'markdown-it';
import { muiTheme as theme } from '@mploy/styles';
import type { JobSearchResultViewModel } from '../../../../types/jobs.types';
import Anchor from '../../foundation/Anchor';
import AnchorTag from '../../foundation/AnchorTag';
import CustomBR from '../../foundation/CustomBR';
import DateFormat from '../../foundation/DateFormat';
import UserContext from '../../context/user.context';
import UserAgent from '../../../utils/UserAgent';
import type { LoggedInUser } from '../../../../types/auth.types';
import {
    CompanyLogoContainer, DesktopOnly, JobItemStyled, JobDetailsStyled, ExtraDataStyle, JobTitleRow,
} from './JobItem.styles';
import { JobItemActions } from './JobItemActions';
import { isEnglishText } from '../../utils/text.utils';
import config from '../../config.client';
import { deleteJob } from '../../../api/job.api';
import JobUpdatedTime from './JobUpdatedTime';

const md = markdownit();

export type JobItemProps = {
    job: JobSearchResultViewModel
    showFullDetails?: boolean
    onReportClick?: (job: JobSearchResultViewModel) => void,
    onApplyClick?: (job: JobSearchResultViewModel) => void,
    onRemoveJobClick?: (job: JobSearchResultViewModel) => void,
    onSaveJobClick?: (job: JobSearchResultViewModel) => void,
    onUnsaveJobClick?: (job: JobSearchResultViewModel) => void,
    onReadMoreClick?: (job: JobSearchResultViewModel) => void,
    lightMode?: boolean,
    showTags?: boolean
    showRemoveJob?: boolean
    showDetailsLink?: boolean
    showPhoneBtn?: boolean
    showRelativeUpdatedAt?: boolean
}

const MAX_LENGTH_DETAILS = 150;

const JobItem = ({
    job, showTags = true, ...props
}: JobItemProps): JSX.Element => {
    const [showPhone, setShowPhone] = useState<boolean>(false);
    const [showFullDetails, setShowFullDetails] = useState<boolean>(!!props.showFullDetails);
    useEffect(
        () => { if (showFullDetails && props.onReadMoreClick) props.onReadMoreClick(job); },
        [showFullDetails],
    );
    let details = `${job.description}${job.requirement ? ` \n דרישות: \n ${job.requirement}` : ''}`;
    let readMore: { text: string, lastLine: string } | null = null;
    const ltr = useMemo(() => isEnglishText(details), [details]);

    if ((!showFullDetails || props.lightMode) && details.length > MAX_LENGTH_DETAILS) {
        details = `${details.slice(0, MAX_LENGTH_DETAILS)}... `;
        const lines = details.split('\n');
        const lastLine = lines.pop();
        details = lines.join('\n');
        readMore = {
            text: ltr ? 'read more' : 'קרא עוד',
            lastLine: lastLine ?? '',
        };
    }

    const user = useContext(UserContext);

    const showNotLoggedInModal = () => {
        window.showGlobalModal(
            'אינך מחובר למערכת',
            'על מנת לצפות בפרטי המשרה יש להירשם לאתר, זה כיף וקל!',
            {
                footer: {
                    leftBtnText: 'הירשם',
                    leftBtnClick: () => {
                        window.modals.toggleRegisterModal();
                    },
                },
            },
        );
    };

    const isUserRegistered = (loggedUser?: LoggedInUser | null) => !!loggedUser;

    const onWhatsAppClick = useCallback(() => {
        if (!isUserRegistered(user)) {
            showNotLoggedInModal();
            return;
        }

        const isMobile = UserAgent.isMobile();

        const whatsappBaseUrl = isMobile ? `https://wa.me/${job.phone.replace('0', '+972')}?`
            : `https://web.whatsapp.com/send?phone=${job.phone.replace('0', '+972')}&`;

        window.open(`${whatsappBaseUrl}text=${encodeURIComponent(
            `‏שלום רב,
אשמח לשמוע פרטים נוספים על המשרה:

${job.description.slice(0, 200)}${job.description.length > 200 ? '...' : ''}

        ${config.baseUrl}wa/jobs/${job.jobid}/candidate`)}`, '_blank');
    }, [job, showNotLoggedInModal, user]);

    const onPhoneClick = useCallback(() => {
        // need to be explicit
        if (!isUserRegistered(user)) {
            showNotLoggedInModal();
        } else if (showPhone) {
            window.location.assign(`tel:${job.phone}`);
        } else setShowPhone(true);
    }, [showPhone, job, user, setShowPhone, showNotLoggedInModal]);

    const onApplyClick = useCallback(jobItem => {
        if (!isUserRegistered(user)) {
            showNotLoggedInModal();
            return;
        }
        props.onApplyClick?.(jobItem);
    }, [user, job, props.onApplyClick, showNotLoggedInModal]);

    const xSmallScreen = useMediaQuery(theme.breakpoints.down('xs'));

    const detailsHtml = useMemo(() => {
        // replacing * with ' * ' to avoid different HTML on client vs server. on server it became <em> on client stayed the same
        const rawHtml = md.render(details);
        const html = sanitizeHtml(
            rawHtml,
            {
                transformTags: {
                    a: sanitizeHtml.simpleTransform('a', { rel: 'nofollow' }),
                },
                allowedAttributes: {
                    a: ['href', 'name', 'target', 'rel'],
                },
            });
        return html;
    }, [details]);

    const aiSearchUrl = `/employer/ai-candidates-search?jobId=${job.jobid}`;

    const companyProfileUrl = job.company?.profileEnabled && job.company.companyid
        ? `/company/${job.company.companyid}`
        : undefined;
    const companyLabel = job.company
        ? `${job.company.name}${job.company.hrCompany ? ' (כח אדם)' : ''}`
        : '';
    const companyLogo = companyProfileUrl && job.company?.logo ? (
        <img
            alt={job.company.name}
            src={`/assets/logos/${job.company.companyid.toString().split('').join('/')}/${job.company.logo}`}
            width={60}
        />
    ) : null;

    return <JobItemStyled data-jobid={job.jobid}>
        {user?.admin ? <><Box display="flex" flexWrap="wrap" alignItems="center">
            <Typography color="red" fontWeight="bold">כלי ניהול:&nbsp;</Typography>
            <Anchor blank href={`/user/editJob/${job.jobid}`} text="עריכת משרה" />
            <Typography>&nbsp;|&nbsp;</Typography>
            <Anchor blank href={'#'} onClick={async ev => {
                ev.preventDefault();
                await deleteJob({ jobid: job.jobid }, true);
                window.showGlobalModal('המחיקה בוצעה בהצלחה', 'המשרה נמחקה מהמערכת.');
            }} text="מחיקת משרה" />
            {job.createdby_userid != null && <>
                <Typography>&nbsp;|&nbsp;</Typography>
                <Anchor blank href={`/backoffice/admin/users?userid=${job.createdby_userid}`} text={`מעסיק #${job.createdby_userid}`} />
            </>}
            <Typography>&nbsp;|&nbsp;</Typography>
            <Anchor blank href={aiSearchUrl} text="חיפוש מועמדים AI" />
        </Box><CustomBR /></> : ''}
        <Grid direction={showFullDetails && !props.onReadMoreClick ? 'column' : 'row'}
            container spacing={2}>
            <Grid item md={props.lightMode ? 12 : 9} xs={12}>
                <JobTitleRow>
                    {!xSmallScreen && !props.lightMode && companyProfileUrl && companyLogo && (
                        <CompanyLogoContainer>
                            <Anchor aria-label={`לעמוד החברה של ${job.company?.name ?? ''}`} href={companyProfileUrl}>
                                {companyLogo}
                            </Anchor>
                        </CompanyLogoContainer>
                    )}
                    <Anchor color="secondary" variant="h5" fontWeight="bold" blank href={`/job/details/${job.jobid}`}>
                        {job.title}
                    </Anchor>
                </JobTitleRow>
                {props.showRelativeUpdatedAt ? <JobUpdatedTime date={job.updated_at} /> : <ExtraDataStyle alignItems="center" display="flex">
                    <EventAvailableOutlinedIcon fontSize="small" color="secondary" />
                    <DesktopOnly>
                        <Typography display="inline">&nbsp;נכון לתאריך</Typography>
                    </DesktopOnly>
                    <>&nbsp;<DateFormat date={job.updated_at} /></>
                </ExtraDataStyle>}
                <ExtraDataStyle alignItems="center" display="flex">
                    <LocationOnOutlinedIcon fontSize="small" color="secondary" />
                    <Typography display="inline">
                        &nbsp;{job.formatted_address}
                        {job.distance && <>{job.distance < 1 ? `, ${job.distance * 1000} מטרים ממיקומך` : `, ${job.distance} ק"מ ממיקומך`}</>}
                    </Typography>
                </ExtraDataStyle>
                {job.company && <ExtraDataStyle alignItems="center" display="flex">
                    <BusinessIcon fontSize="small" color="secondary" />
                    {companyProfileUrl ? (
                        <Anchor color="inherit" href={companyProfileUrl} text={` ${companyLabel}`} />
                    ) : (
                        <Typography display="inline">&nbsp;{companyLabel}</Typography>
                    )}
                </ExtraDataStyle>}
                <JobDetailsStyled dirAlign={ltr ? 'ltr' : 'rtl'}>
                    {readMore ? <div key="read-more">
                        <div dangerouslySetInnerHTML={{ __html: detailsHtml }} />
                        <div>
                            <p style={{ display: 'inline' }}>{readMore.lastLine}&nbsp;</p>
                            <Anchor color={theme.palette.common.yellowText} href="#" text={readMore.text} onClick={ev => {
                                ev.preventDefault();
                                setShowFullDetails(true);
                            }} />
                        </div>
                    </div> : <div key="no-read-more" style={{ textAlign: ltr ? 'left' : 'right' }}>
                        <div dangerouslySetInnerHTML={{ __html: detailsHtml }} />
                    </div>}
                </JobDetailsStyled>
                {showTags && !props.lightMode && job?.tags?.length && <>
                    <CustomBR />
                    <DesktopOnly>
                        {job.tags.map(pt => <AnchorTag display={pt.display} key={pt.tag} tag={pt.tag} />)}
                    </DesktopOnly>
                </>}
            </Grid>
            {!props.lightMode && <>
                <Grid display="flex" direction="column" alignContent="center" item container md={3} xs={12}>
                    <DesktopOnly>
                        {/* <Grid item md={1} width="1px"> */}
                        <Divider orientation="vertical" style={{ marginLeft: '10px' }} />
                        {/* </Grid> */}
                    </DesktopOnly>
                    <JobItemActions
                        job={job}
                        onSaveJobClick={props.onSaveJobClick}
                        onUnsaveJobClick={props.onUnsaveJobClick}
                        onRemoveJobClick={props.onRemoveJobClick}
                        onReportClick={props.onReportClick}
                        showDetailsLink={props.showDetailsLink}
                        showRemoveJob={props.showRemoveJob}
                        onApplyClick={onApplyClick}
                        onWhatsAppClick={onWhatsAppClick}
                        showPhoneBtn={props.showPhoneBtn}
                        onPhoneClick={onPhoneClick}
                        showPhone={showPhone}
                    />
                </Grid>
            </>}
        </Grid>
    </JobItemStyled>;
};

export default memo(JobItem);
